import type { LocalStore } from '@nandanam/sync';

/** Production device cache: IndexedDB (works in browsers, Electron and Android WebView). */
export class IndexedDbStore implements LocalStore {
  private db: Promise<IDBDatabase>;
  constructor(name = 'nandanam-farms') {
    this.db = new Promise((res, rej) => {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => req.result.createObjectStore('kv');
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
    // Ask the browser not to evict the cache under storage pressure (best effort).
    navigator.storage?.persist?.().catch(() => {});
  }
  private async tx<T>(mode: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.db;
    return new Promise((res, rej) => {
      const r = f(db.transaction('kv', mode).objectStore('kv'));
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
  }
  async load(key: string) { return (await this.tx('readonly', s => s.get(key))) ?? null; }
  async save(key: string, value: unknown) { await this.tx('readwrite', s => s.put(structuredClone(value), key)); }
  async remove(key: string) { await this.tx('readwrite', s => s.delete(key)); }
}

/** Demo mode only: keeps the demo in this browser's localStorage, separate from any real account. */
export class LocalStorageStore implements LocalStore {
  constructor(private prefix: string) {}
  async load(k: string) { try { const v = localStorage.getItem(this.prefix + k); return v ? JSON.parse(v) : null; } catch { return null; } }
  async save(k: string, v: unknown) { try { localStorage.setItem(this.prefix + k, JSON.stringify(v)); } catch { /* storage full or blocked: demo keeps working in memory */ } }
  async remove(k: string) { try { localStorage.removeItem(this.prefix + k); } catch { /* ignore */ } }
}
